/* raw-stand-data.js - The Raw Comics Stand: the book list (window.RawStandData).
   Builder D, 2026-10-07 (mt4 work order D; Jake M-092..M-096). Plain JS, no libraries, no build step.
   Design note per S-build (~/AI/FLEET/standards/S-build.md 1b usability first, 3a token-lean, 3e explicit
   interfaces, 4b tested with MUST-FAILs: tools/test_stand_data.js): one job, the data. The UI is raw-stand.js.

   load({ onProgress, onUpdate, feed, force }) -> Promise<{ books, version, complete, fromCache, done }>
     Resolves as soon as there is something to draw: the cached list (return visit) or feed page 1
     (first visit). The rest streams in behind it:
       onProgress(booksSoFar, { page, pages, loaded, total, done, replaced })   after every page
       done   -> Promise<{ books, version, changed }>   the whole list (or the revalidated one)
     A cached list older than CONFIG.cacheFreshMs is shown at once and re-checked in the background; if it
     changed, onUpdate(books, version) and onProgress(books, { replaced: true, done: true }) fire.
   detail(book) -> Promise<book>   adds d (plain text, one line per paragraph) and imgs[] from
     /products/<handle>.js, and refreshes p / vid / avail / c from the live product. Cached per handle.
   offFor(tags) -> 10 | 20         the store's automatic discounts, from CONFIG.off (one place).
   cdn(url, width) -> url with &width= (Shopify's image CDN sizes it, WebP where the browser takes it).

   Book: { id, handle, h, t, v, tags, p, c, off, img, d, imgs, vid, avail, a, g }   (g = the grade metafield, '' when the book has none)
     id = PRODUCT id. vid = the variant to put in the cart. h and a are the prototype's names for handle
     and avail, kept so ported prototype code reads unchanged. NOTE: the prototype's books.json used the
     VARIANT id as `id`; here id is the product id and the variant is vid.
   Errors reject with an Error whose message is written for a shopper. Storage is optional everywhere. */
(function (root) {
  'use strict';

  var CONFIG = {
    // The store's automatic discounts (Jake M-092: "the 10% and 20% automatic discounts still apply").
    // First rule whose tag matches wins; otherwise fallback. Change it HERE only.
    off: { rules: [{ tags: ['gold', 'wall'], off: 10 }], fallback: 20 },
    hideSoldOut: true,                  // plan: "Out-of-stock books: hidden"
    concurrency: 3,                     // feed pages in flight after page 1
    retries: 2,                         // per request, on network errors, 429 and 5xx
    retryDelayMs: 600,                  // doubled each retry
    timeoutMs: 20000,
    cacheKey: 'rc-stand-books',         // { schema, feed, version, at, rows }
    metaKey: 'rc-stand-books-meta',     // { feed, version, at } - read by the inline early fetch
    cacheSchema: 1,
    cacheFreshMs: 10 * 60 * 1000        // younger than this: no network at all on a return visit
  };

  var detailCache = Object.create(null);

  // ---------- small helpers ----------
  function shopRoot() {
    var s = root.Shopify && root.Shopify.routes && root.Shopify.routes.root;
    return typeof s === 'string' && s ? s : '/';
  }
  function absUrl(u) {
    if (!u || typeof u !== 'string') return '';
    if (u.indexOf('//') === 0) u = 'https:' + u;
    // no size on the stored URL: cdn() adds the width each screen needs
    return u.replace(/([?&])width=\d+(&|$)/, function (m, a, b) { return b ? a : ''; });
  }
  function cdn(url, width) {
    if (!url) return '';
    return width ? url + (url.indexOf('?') >= 0 ? '&' : '?') + 'width=' + Math.round(width) : url;
  }
  function offFor(tags) {
    var t = Array.isArray(tags) ? tags : [];
    for (var i = 0; i < CONFIG.off.rules.length; i++) {
      var r = CONFIG.off.rules[i];
      for (var j = 0; j < r.tags.length; j++) if (t.indexOf(r.tags[j]) >= 0) return r.off;
    }
    return CONFIG.off.fallback;
  }
  function centsToStr(c) {
    c = Math.round(Number(c) || 0);
    return Math.floor(c / 100) + '.' + String(100 + (c % 100)).slice(1);
  }
  // FNV-1a, 32-bit: a cheap fingerprint of the feed text, so a price edit changes the version
  function fnv(str, h) {
    h = h === undefined ? 0x811c9dc5 : h;
    for (var i = 0; i < str.length; i++) { h ^= str.charCodeAt(i); h = Math.imul(h, 0x01000193) >>> 0; }
    return h >>> 0;
  }
  var ENT = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ', mdash: '—', ndash: '–',
    hellip: '…', rsquo: '’', lsquo: '‘', rdquo: '”', ldquo: '“', copy: '©',
    reg: '®', trade: '™', eacute: 'é', deg: '°', frac12: '½', times: '×' };
  // Product description HTML -> plain text, one line per paragraph / <br> / list item (the prototype's d).
  function toPlainText(html) {
    if (!html) return '';
    var s = String(html)
      .replace(/<(script|style)[\s\S]*?<\/\1>/gi, '')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/(p|div|li|h[1-6]|tr|blockquote)>/gi, '\n')
      .replace(/<li[^>]*>/gi, '• ')
      .replace(/<[^>]+>/g, '')
      .replace(/&(#x[0-9a-f]+|#\d+|[a-z0-9]+);/gi, function (m, e) {
        if (e[0] === '#') {
          var n = e[1] === 'x' || e[1] === 'X' ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10);
          try { return String.fromCodePoint(n); } catch (x) { return m; }
        }
        return Object.prototype.hasOwnProperty.call(ENT, e.toLowerCase()) ? ENT[e.toLowerCase()] : m;
      });
    return s.split('\n').map(function (l) { return l.replace(/[ \t ]+/g, ' ').trim(); })
      .filter(Boolean).join('\n');
  }

  // ---------- storage (optional; never throws) ----------
  function store() { try { return root.localStorage || null; } catch (e) { return null; } }
  function readCache(feed) {
    try {
      var ls = store(); if (!ls) return null;
      var c = JSON.parse(ls.getItem(CONFIG.cacheKey) || 'null');
      if (!c || c.schema !== CONFIG.cacheSchema || c.feed !== feed || !Array.isArray(c.rows)) return null;
      return c;
    } catch (e) { return null; }
  }
  function writeCache(feed, version, rows) {
    var at = Date.now();
    try {
      var ls = store(); if (!ls) return false;
      ls.setItem(CONFIG.cacheKey, JSON.stringify({ schema: CONFIG.cacheSchema, feed: feed, version: version, at: at, rows: rows }));
      ls.setItem(CONFIG.metaKey, JSON.stringify({ feed: feed, version: version, at: at }));
      return true;
    } catch (e) {                         // quota full or storage blocked: run without a cache
      try { store().removeItem(CONFIG.cacheKey); store().removeItem(CONFIG.metaKey); } catch (x) {}
      return false;
    }
  }
  function touchCache(c) {
    try {
      c.at = Date.now();
      var ls = store(); if (!ls) return;
      ls.setItem(CONFIG.cacheKey, JSON.stringify(c));
      ls.setItem(CONFIG.metaKey, JSON.stringify({ feed: c.feed, version: c.version, at: c.at }));
    } catch (e) {}
  }
  function clearCache() {
    try { var ls = store(); if (ls) { ls.removeItem(CONFIG.cacheKey); ls.removeItem(CONFIG.metaKey); } } catch (e) {}
  }

  // ---------- network ----------
  function wait(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }
  function httpError(status, what) {
    var e = new Error(status === 404 ? 'The ' + what + ' could not be found (HTTP 404).'
      : status >= 500 || status === 429 ? 'The shop is busy right now (HTTP ' + status + '). Please try again in a moment.'
      : 'The ' + what + ' could not be loaded (HTTP ' + status + ').');
    e.status = status;
    return e;
  }
  function getText(url, what) {
    var tries = 0;
    function once() {
      var f = root.fetch;
      if (typeof f !== 'function') return Promise.reject(new Error('This browser cannot load the ' + what + '.'));
      var ctl = typeof AbortController === 'function' ? new AbortController() : null;
      var timer = ctl ? setTimeout(function () { ctl.abort(); }, CONFIG.timeoutMs) : null;
      return Promise.resolve()
        .then(function () {
          return f(url, { credentials: 'same-origin', headers: { Accept: '*/*' }, signal: ctl ? ctl.signal : undefined });
        })
        .then(function (r) {
          if (!r || !r.ok) throw httpError(r ? r.status : 0, what);
          return r.text();
        }, function (err) {
          var e = new Error('Could not reach the shop to load the ' + what + '. Check your connection and try again.');
          e.cause = err; e.network = true;
          throw e;
        })
        .then(function (t) { if (timer) clearTimeout(timer); return t; },
              function (e) { if (timer) clearTimeout(timer); throw e; });
    }
    function attempt() {
      return once().catch(function (e) {
        var retry = e.network || e.status === 429 || e.status >= 500;
        if (!retry || tries >= CONFIG.retries) throw e;
        tries++;
        return wait(CONFIG.retryDelayMs * Math.pow(2, tries - 1)).then(attempt);
      });
    }
    return attempt();
  }
  // Liquid output is JSON, but tolerate anything Shopify might wrap around it (e.g. a preview bar).
  function parseFeed(text) {
    var j;
    try { j = JSON.parse(text); } catch (e) {
      var a = text.indexOf('{'), b = text.lastIndexOf('}');
      try { j = a >= 0 && b > a ? JSON.parse(text.slice(a, b + 1)) : null; } catch (x) { j = null; }
    }
    if (!j || !Array.isArray(j.books) || typeof j.pages !== 'number') {
      throw new Error('The comics list came back in an unexpected format. Please reload the page.');
    }
    return j;
  }

  // ---------- rows -> books ----------
  function toBook(r) {
    var tags = Array.isArray(r.tags) ? r.tags.map(function (t) { return String(t).toLowerCase(); }) : [];
    var avail = r.avail === true;
    return {
      id: r.id, handle: r.handle, h: r.handle, t: r.t || '', v: r.v || '', tags: tags,
      p: typeof r.p === 'string' ? r.p : centsToStr((Number(r.p) || 0) * 100), c: r.c || null,
      off: offFor(tags), img: absUrl(r.img), d: '', imgs: [], vid: r.vid, avail: avail, a: avail,
      g: r.g == null ? '' : String(r.g).trim()                     // Jake M-151: only a grade the store has; never guessed
    };
  }
  function booksFrom(rows) {
    var seen = Object.create(null), out = [];
    for (var i = 0; i < rows.length; i++) {
      var r = rows[i];
      if (!r || r.id == null || seen[r.id]) continue;    // dedupe: pages can shift while a list streams
      seen[r.id] = 1;
      if (CONFIG.hideSoldOut && r.avail !== true) continue;
      out.push(toBook(r));
    }
    return out;
  }

  function feedFromPage() {
    var el = root.document && root.document.querySelector && root.document.querySelector('.raw-stand[data-feed]');
    return (el && el.getAttribute('data-feed')) || '/collections/all?view=stand-data';
  }
  function pageUrl(feed, n) { return feed + (feed.indexOf('?') >= 0 ? '&' : '?') + 'page=' + n; }

  // Page 1: adopt the inline early fetch from snippets/raw-stand.liquid when it asked for the same URL.
  function fetchPage(feed, n) {
    var url = pageUrl(feed, n), early = root.RawStandEarly, primary;
    if (n === 1 && early && early.url === url && early.text) {
      root.RawStandEarly = null;
      primary = early.text.catch(function () { return getText(url, 'comics list'); });
    } else primary = getText(url, 'comics list');
    // The ?view=stand-data template only answers on the theme that ships it. In the theme editor and some
    // previews the request lands on another theme (404 or an HTML page), so fall back to Shopify's public
    // products.json, which every theme serves, and reshape it into the same feed format.
    return primary.then(function (t) {
      try { parseFeed(t); return t; } catch (e) { return fallbackPage(n).catch(function () { throw e; }); }
    }, function (e) {
      if (!e || e.status !== 404) throw e;   // only a missing template falls back; 5xx/429/network errors surface as-is
      return fallbackPage(n).catch(function () { throw e; });   // if the fallback fails too, report the original problem
    });
  }
  function shopCount() {
    var el = root.document && root.document.querySelector && root.document.querySelector('.raw-stand[data-count]');
    return el ? (parseInt(el.getAttribute('data-count'), 10) || 0) : 0;
  }
  function fallbackPage(n) {
    var rootPath = (root.Shopify && root.Shopify.routes && root.Shopify.routes.root) || '/';
    var url = rootPath + 'collections/all/products.json?limit=250&page=' + n;
    return getText(url, 'comics list').then(function (t) {
      var j = JSON.parse(t), ps = (j && j.products) || [];
      var count = shopCount();
      var pages = count ? Math.max(1, Math.ceil(count / 250)) : (ps.length === 250 ? n + 1 : n);
      var books = ps.map(function (p) {
        var vs = p.variants || [], v = vs.filter(function (x) { return x.available; })[0] || vs[0] || {};
        var tags = Array.isArray(p.tags) ? p.tags : String(p.tags || '').split(/,\s*/).filter(Boolean);
        var price = String(v.price || '0'), cp = v.compare_at_price;
        return { id: p.id, handle: p.handle, t: p.title, v: p.vendor, tags: tags, p: price,
          c: cp && parseFloat(cp) > parseFloat(price) ? String(cp) : undefined,
          vid: v.id, avail: v.available === true, img: (p.images && p.images[0] && p.images[0].src) || '' };
      });
      return JSON.stringify({ page: n, pages: pages, count: count, books: books, v: count + '-0' });
    });
  }

  // Streams every page. Page 1 first, then the rest CONFIG.concurrency at a time, in page order for onPage.
  function streamAll(feed, onFirst, onPage) {
    return fetchPage(feed, 1).then(function (t1) {
      var j1 = parseFeed(t1), pages = Math.max(1, j1.pages | 0);
      var texts = [t1], rowsBy = [j1.books], newest = 0, count = j1.count | 0;
      function note(j) { var m = /-(\d+)$/.exec(String(j.v || '')); if (m) newest = Math.max(newest, +m[1]); }
      note(j1);
      if (onFirst) onFirst(j1.books, pages);
      var next = 2, loaded = 1, failed = null;
      function allRows() { var a = []; for (var i = 0; i < rowsBy.length; i++) if (rowsBy[i]) a = a.concat(rowsBy[i]); return a; }
      function worker() {
        if (failed || next > pages) return Promise.resolve();
        var n = next++;
        return fetchPage(feed, n).then(function (t) {
          var j = parseFeed(t);
          texts[n - 1] = t; rowsBy[n - 1] = j.books; note(j); loaded++;
          if (onPage) onPage(allRows(), { page: n, pages: pages, loaded: loaded, done: loaded === pages });
          return worker();
        }, function (e) { failed = failed || e; throw e; });
      }
      var ws = [];
      for (var k = 0; k < Math.min(CONFIG.concurrency, pages - 1); k++) ws.push(worker());
      return Promise.all(ws).then(function () {
        var h = 0x811c9dc5;
        for (var i = 0; i < texts.length; i++) h = fnv(texts[i], h);
        return { rows: allRows(), version: count + '-' + newest + '-' + h.toString(36), pages: pages };
      });
    });
  }

  function load(opts) {
    opts = opts || {};
    var feed = opts.feed || feedFromPage();
    var onProgress = typeof opts.onProgress === 'function' ? opts.onProgress : null;
    var onUpdate = typeof opts.onUpdate === 'function' ? opts.onUpdate : null;
    function safe(fn, a, b) { try { if (fn) fn(a, b); } catch (e) { if (root.console) console.error(e); } }

    var cached = opts.force ? null : readCache(feed);
    if (cached) {
      var cbooks = booksFrom(cached.rows);
      var fresh = Date.now() - (cached.at || 0) < CONFIG.cacheFreshMs;
      var done = fresh
        ? Promise.resolve({ books: cbooks, version: cached.version, changed: false })
        : streamAll(feed, null, null).then(function (res) {
            if (res.version === cached.version) { touchCache(cached); return { books: cbooks, version: cached.version, changed: false }; }
            writeCache(feed, res.version, res.rows);
            var nb = booksFrom(res.rows);
            safe(onUpdate, nb, res.version);
            safe(onProgress, nb, { page: res.pages, pages: res.pages, loaded: res.pages, total: nb.length, done: true, replaced: true });
            return { books: nb, version: res.version, changed: true };
          });
      done.catch(function () {});        // a failed background re-check keeps the cached list; callers may still read done
      return Promise.resolve({ books: cbooks, version: cached.version, complete: true, fromCache: true, done: done });
    }

    return new Promise(function (resolve, reject) {
      var resolved = false;
      // onFirst runs in a later microtask than this assignment, so `done` is set by the time it is read
      var done = streamAll(feed, function (rows, pages) {
        var b = booksFrom(rows);
        safe(onProgress, b, { page: 1, pages: pages, loaded: 1, total: b.length, done: pages === 1, replaced: false });
        if (pages > 1) { resolved = true; resolve({ books: b, version: null, complete: false, fromCache: false, done: done }); }
      }, function (rows, info) {
        var b = booksFrom(rows);
        info.total = b.length; info.replaced = false;
        safe(onProgress, b, info);
      }).then(function (res) {
        writeCache(feed, res.version, res.rows);
        return { books: booksFrom(res.rows), version: res.version, changed: true };
      });
      done.catch(function () {});
      done.then(function (res) {           // a one-page list resolves once, complete, with its version
        if (!resolved) { resolved = true; resolve({ books: res.books, version: res.version, complete: true, fromCache: false, done: done }); }
      }, function (e) {                    // page 1 failed: load() rejects; a later page failed: done rejects
        if (!resolved) { resolved = true; reject(e); }
      });
    });
  }

  function detail(book) {
    if (!book || !book.handle) return Promise.reject(new Error('That book has no product page to load.'));
    if (book._detailed) return Promise.resolve(book);
    var h = book.handle;
    if (!detailCache[h]) {
      detailCache[h] = getText(shopRoot() + 'products/' + encodeURIComponent(h) + '.js', 'book details')
        .then(function (t) {
          var j;
          try { j = JSON.parse(t); } catch (e) { throw new Error('The book details came back in an unexpected format.'); }
          return j;
        });
      detailCache[h].catch(function () { delete detailCache[h]; });   // a failure can be retried
    }
    return detailCache[h].then(function (j) {
      book.d = toPlainText(j.description || '');
      book.imgs = (Array.isArray(j.images) ? j.images : []).map(absUrl).filter(Boolean);
      if (!book.imgs.length && book.img) book.imgs = [book.img];
      var vs = Array.isArray(j.variants) ? j.variants : [];
      var v = vs.filter(function (x) { return x && x.available; })[0] || vs[0];
      if (v) {
        book.vid = v.id;
        book.p = centsToStr(v.price);
        book.c = v.compare_at_price && v.compare_at_price > v.price ? centsToStr(v.compare_at_price) : null;
      }
      book.avail = book.a = j.available === true;
      book._detailed = true;
      return book;
    });
  }

  root.RawStandData = {
    config: CONFIG,
    load: load,
    detail: detail,
    offFor: offFor,
    cdn: cdn,
    clearCache: clearCache,
    // exposed for tools/test_stand_data.js and for builder U's harness
    _internals: { toPlainText: toPlainText, absUrl: absUrl, parseFeed: parseFeed, booksFrom: booksFrom, toBook: toBook, centsToStr: centsToStr, pageUrl: pageUrl }
  };
})(typeof window !== 'undefined' ? window : globalThis);
